import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, XCircle, ArrowRight, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { registerKitService, ROUTER_VENDORS, RouterVendor } from '@/api/services/registerKitService';
import apiClient from '@/api/client';
import { AxiosError } from 'axios';
import { getErrorMessage } from '@/utils/errorUtils';

export default function HotspotRegistration() {
  const { theme } = useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false); // guards against spam-click
  const [checking, setChecking] = useState(true); // briefly true while we check /current
  const [existingRegistration, setExistingRegistration] = useState<{
    registrationId: string;
    status: string;
    currentStep: number;
  } | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [formData, setFormData] = useState({
    deviceBrand: '',
    deviceModel: '',
    macAddress: '',
    serialNumber: '',
    // Required for approval: ISO country code sets the node's currency/plans,
    // coordinates place the node.
    countryCode: '',
    latitude: '',
    longitude: ''
  });

  // KX-33 / T-26: router vendor is picked separately so it can drive the
  // connectivity probe. Defaults to "generic" so the picker is never empty.
  const [vendor, setVendor] = useState<RouterVendor>('generic');

  // Check if user has an in-progress registration. If yes, offer to resume
  // rather than silently starting a new one (which would orphan the old).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await apiClient.get('/registerkit/current');
        const reg = response.data?.data;
        if (!cancelled && reg && reg.status === 'in-progress' && !reg.isExpired) {
          setExistingRegistration({
            registrationId: reg.registrationId,
            status: reg.status,
            currentStep: reg.currentStep,
          });
          // Restore session storage so step 2/3 work seamlessly
          sessionStorage.setItem('registrationId', reg.registrationId);
        }
      } catch (err: unknown) {
        // 404 or any error -> no existing registration, fresh start
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  /**
   * Formats MAC Address string as user types (Adds colons automatically)
   */
  const formatMacAddress = (value: string) => {
    const cleanValue = value.replace(/[^a-fA-F0-9]/g, '').toUpperCase();
    const matches = cleanValue.match(/.{1,2}/g);
    return matches ? matches.join(':').slice(0, 17) : cleanValue;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: name === 'macAddress' ? formatMacAddress(value) : value
    }));
  };

  const validateForm = () => {
    const { deviceBrand, deviceModel, macAddress, serialNumber, countryCode, latitude, longitude } = formData;
    if (!deviceBrand.trim() || !deviceModel.trim() || !macAddress.trim() || !serialNumber.trim()) {
      showMessage('error', t('operator.registerKit.step1.errors.required', {
        defaultValue: 'Please fill in all required fields.',
      }));
      return false;
    }
    // Basic MAC Address validation (XX:XX:XX:XX:XX:XX)
    const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    if (!macRegex.test(macAddress.trim())) {
      showMessage('error', t('operator.registerKit.step1.errors.invalidMac', {
        defaultValue: 'Please enter a valid MAC address (XX:XX:XX:XX:XX:XX).',
      }));
      return false;
    }
    if (serialNumber.trim().length < 4) {
      showMessage('error', t('operator.registerKit.step1.errors.shortSerial', {
        defaultValue: 'Serial number looks too short. Please double-check it.',
      }));
      return false;
    }
    // Country code is required — it decides the node's currency and plans and must
    // be set before the kit can be approved. Must be a 2-letter ISO code.
    if (!/^[A-Za-z]{2}$/.test(countryCode.trim())) {
      showMessage('error', t('operator.registerKit.step1.errors.invalidCountry', {
        defaultValue: 'Please enter a valid 2-letter country code (e.g. NG).',
      }));
      return false;
    }
    // Coordinates are required to place the node. Validate numeric ranges.
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (!latitude.trim() || Number.isNaN(lat) || lat < -90 || lat > 90) {
      showMessage('error', t('operator.registerKit.step1.errors.invalidLatitude', {
        defaultValue: 'Please enter a valid latitude between -90 and 90.',
      }));
      return false;
    }
    if (!longitude.trim() || Number.isNaN(lng) || lng < -180 || lng > 180) {
      showMessage('error', t('operator.registerKit.step1.errors.invalidLongitude', {
        defaultValue: 'Please enter a valid longitude between -180 and 180.',
      }));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (submitting || loading) return; // debounce spam-clicks
    if (!validateForm()) return;

    setSubmitting(true);
    setLoading(true);
    try {
      const response = await registerKitService.registerStep1({
        deviceBrand: formData.deviceBrand.trim(),
        deviceModel: formData.deviceModel.trim(),
        macAddress: formData.macAddress.trim().toUpperCase(),
        serialNumber: formData.serialNumber.trim().toUpperCase(),
        // Required for approval: ISO country code (uppercased) + real coordinates.
        countryCode: formData.countryCode.trim().toUpperCase(),
        latitude: Number(formData.latitude),
        longitude: Number(formData.longitude),
        vendor,
      });

      showMessage('success', response.message || t('operator.registerKit.step1.success', {
        defaultValue: 'Device details saved. Checking your connection...',
      }));

      if (response.registrationId) {
        sessionStorage.setItem('registrationId', response.registrationId);
        setTimeout(() => {
          // Go to the live connectivity check ("we can see your router") before KYC.
          navigate('/operator/registerKitConnectivity');
        }, 1200);
      } else {
        throw new Error(t('operator.registerKit.step1.errors.noRegistrationId', {
          defaultValue: 'Server did not return a registration ID. Please try again.',
        }));
      }
    } catch (error: unknown) {
      const axiosErr = error instanceof AxiosError
        ? error as AxiosError<{ message?: string }>
        : null;
      const apiMsg = axiosErr?.response?.data?.message;
      const statusCode = axiosErr?.response?.status;
      let errorMsg = apiMsg || t('operator.registerKit.step1.errors.generic', {
        defaultValue: 'Failed to register device details. Please try again.',
      });
      // Map common backend errors to friendlier copy
      if (statusCode === 409) {
        errorMsg = t('operator.registerKit.step1.errors.duplicate', {
          defaultValue: 'This MAC address or serial number is already registered. Please double-check or contact support.',
        });
      } else if (statusCode === 401) {
        errorMsg = t('operator.registerKit.step1.errors.unauthorized', {
          defaultValue: 'Your session expired. Please sign in again.',
        });
        setTimeout(() => navigate('/signin'), 2000);
      }
      showMessage('error', errorMsg);
    } finally {
      setLoading(false);
      // Keep submitting=true for ~1s so the brief success state doesn't get
      // immediately re-clicked into a second submit.
      setTimeout(() => setSubmitting(false), 1000);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('operator.registerKit.step1.metaTitle', { defaultValue: 'Hotspot Registration | KonectX' })}</title>
        <meta
          name="description"
          content={t('operator.registerKit.step1.metaDescription', {
            defaultValue: 'Register your hotspot kit to start earning on the KonectX network.',
          })}
        />
      </Helmet>

      <div className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="max-w-[1400px] mx-auto px-12 py-12">
          
          {/* Global Message */}
          {message && (
            <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-4 duration-300 ${
              message.type === 'success' 
                ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800'
            }`}>
              {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              <span className="text-sm font-medium">{message.text}</span>
            </div>
          )}

          <div className="mb-8">
            <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {t('operator.registerKit.step1.heading', { defaultValue: 'Register Your Hotspot Kit' })}
            </h1>
            <p className={`text-lg mt-3 font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('operator.registerKit.step1.subtitle', { defaultValue: 'Step 1 of 3 • Device Details' })}
            </p>
          </div>

          {/* Resume-existing-registration banner */}
          {checking ? (
            <div className="mb-8 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('operator.registerKit.step1.checking', { defaultValue: 'Checking for existing registration...' })}
            </div>
          ) : existingRegistration ? (
            <div className="mb-8 rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-300">
                  {t('operator.registerKit.step1.resumeTitle', { defaultValue: 'You have a registration in progress' })}
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                  {t('operator.registerKit.step1.resumeBody', {
                    defaultValue: 'Continue where you left off, or start fresh with new device details below.',
                  })}
                </p>
                <button
                  onClick={() => {
                    // KX-39: KYC step removed from activation. Resume into the
                    // connectivity check (old "step 2" slot) or payment.
                    const route =
                      existingRegistration.currentStep === 3
                        ? '/operator/registerKitStep3'
                        : '/operator/registerKitConnectivity';
                    navigate(route);
                  }}
                  className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 dark:text-blue-300 hover:underline"
                >
                  {t('operator.registerKit.step1.resumeButton', { defaultValue: 'Resume registration' })}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : null}

          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-sm font-bold text-[#2E5A88] uppercase tracking-wider">
                {t('operator.registerKit.progress.step1', { defaultValue: 'Step 1: Device' })}
              </span>
              <span className={`text-sm font-bold uppercase tracking-wider ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>
                {t('operator.registerKit.progress.step2', { defaultValue: 'Step 2: Connectivity' })}
              </span>
              <span className={`text-sm font-bold uppercase tracking-wider ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>
                {t('operator.registerKit.progress.step3', { defaultValue: 'Step 3: Payment' })}
              </span>
            </div>
            <div className={`w-full rounded-full h-2 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'}`}>
              <div className="bg-[#2E5A88] h-2 rounded-full transition-all duration-500" style={{ width: '33.33%' }}></div>
            </div>
          </div>

          {/* Form Content */}
          <div className={`rounded-2xl border ${theme === 'dark' ? 'bg-[#111] border-gray-800' : 'bg-white border-gray-200 shadow-xl'} p-8 md:p-12`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

              {/* Router vendor picker — drives the connectivity probe (KX-33/T-26) */}
              <div className="space-y-3 md:col-span-2">
                <label className={`block text-sm font-bold uppercase tracking-tight ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('operator.registerKit.step1.fields.vendor', { defaultValue: 'Router Vendor' })} <span className="text-red-500">*</span>
                </label>
                <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                  {t('operator.registerKit.step1.vendorHint', {
                    defaultValue: 'Pick the brand of the router you are bringing. This lets us verify it later.',
                  })}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {ROUTER_VENDORS.map((v) => {
                    const selected = vendor === v;
                    return (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setVendor(v)}
                        aria-pressed={selected}
                        className={`rounded-xl border-2 px-3 py-4 text-sm font-semibold transition-colors text-center ${
                          selected
                            ? 'border-[#2E5A88] bg-[#2E5A88] text-white'
                            : theme === 'dark'
                              ? 'border-gray-700 bg-[#1c1c1c] text-gray-300 hover:border-gray-600'
                              : 'border-gray-300 bg-gray-50 text-gray-700 hover:border-[#2E5A88]'
                        }`}
                      >
                        {t(`operator.registerKit.step1.vendors.${v}`, {
                          defaultValue: v.charAt(0).toUpperCase() + v.slice(1),
                        })}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Device Brand */}
              <div className="space-y-3">
                <label className={`block text-sm font-bold uppercase tracking-tight ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('operator.registerKit.step1.fields.deviceBrand', { defaultValue: 'Device Brand' })} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="deviceBrand"
                  value={formData.deviceBrand}
                  onChange={handleChange}
                  placeholder={t('operator.registerKit.step1.placeholders.deviceBrand', { defaultValue: 'e.g. TP-Link, Helium, SenseCAP' })}
                  className={`w-full px-5 py-4 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition ${
                    theme === 'dark' 
                      ? 'bg-[#1c1c1c] border-gray-700 text-white placeholder-gray-600' 
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {/* Device Model */}
              <div className="space-y-3">
                <label className={`block text-sm font-bold uppercase tracking-tight ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('operator.registerKit.step1.fields.deviceModel', { defaultValue: 'Device Model' })} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="deviceModel"
                  value={formData.deviceModel}
                  onChange={handleChange}
                  placeholder={t('operator.registerKit.step1.placeholders.deviceModel', { defaultValue: 'e.g. Archer C6, M1 Hotspot' })}
                  className={`w-full px-5 py-4 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition ${
                    theme === 'dark' 
                      ? 'bg-[#1c1c1c] border-gray-700 text-white placeholder-gray-600' 
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {/* MAC Address */}
              <div className="space-y-3">
                <label className={`block text-sm font-bold uppercase tracking-tight ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('operator.registerKit.step1.fields.macAddress', { defaultValue: 'MAC Address' })} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="macAddress"
                  value={formData.macAddress}
                  onChange={handleChange}
                  placeholder="00:00:00:00:00:00"
                  className={`w-full px-5 py-4 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition font-mono ${
                    theme === 'dark' 
                      ? 'bg-[#1c1c1c] border-gray-700 text-white placeholder-gray-600' 
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {/* Serial Number */}
              <div className="space-y-3">
                <label className={`block text-sm font-bold uppercase tracking-tight ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('operator.registerKit.step1.fields.serialNumber', { defaultValue: 'Serial Number' })} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="serialNumber"
                  value={formData.serialNumber}
                  onChange={handleChange}
                  placeholder={t('operator.registerKit.step1.placeholders.serialNumber', { defaultValue: 'Found on the back of your device' })}
                  className={`w-full px-5 py-4 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition ${
                    theme === 'dark' 
                      ? 'bg-[#1c1c1c] border-gray-700 text-white placeholder-gray-600' 
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {/* Country code — decides the node's currency and plans; required for approval */}
              <div className="space-y-2">
                <label className={`block text-sm font-bold uppercase tracking-tight ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('operator.registerKit.step1.fields.countryCode', { defaultValue: 'Country Code (ISO)' })} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                  maxLength={2}
                  placeholder={t('operator.registerKit.step1.placeholders.countryCode', { defaultValue: 'e.g. NG' })}
                  className={`w-full px-5 py-4 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition uppercase ${
                    theme === 'dark' 
                      ? 'bg-[#1c1c1c] border-gray-700 text-white placeholder-gray-600' 
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {/* Latitude — required to place the node */}
              <div className="space-y-2">
                <label className={`block text-sm font-bold uppercase tracking-tight ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('operator.registerKit.step1.fields.latitude', { defaultValue: 'Latitude' })} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  step="any"
                  placeholder="6.5244"
                  className={`w-full px-5 py-4 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition ${
                    theme === 'dark' 
                      ? 'bg-[#1c1c1c] border-gray-700 text-white placeholder-gray-600' 
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {/* Longitude — required to place the node */}
              <div className="space-y-2">
                <label className={`block text-sm font-bold uppercase tracking-tight ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('operator.registerKit.step1.fields.longitude', { defaultValue: 'Longitude' })} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  step="any"
                  placeholder="3.3792"
                  className={`w-full px-5 py-4 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition ${
                    theme === 'dark' 
                      ? 'bg-[#1c1c1c] border-gray-700 text-white placeholder-gray-600' 
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end mt-12">
              <button
                onClick={handleSubmit}
                disabled={loading || submitting}
                className="group relative px-12 py-4 bg-[#2E5A88] hover:bg-[#244a73] text-white font-bold rounded-xl text-lg transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-3 shadow-lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{t('operator.registerKit.step1.submitting', { defaultValue: 'Verifying...' })}</span>
                  </>
                ) : (
                  <>
                    <span>{t('operator.registerKit.step1.submit', { defaultValue: 'Continue to Connectivity Check' })}</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
